import {AxiosError} from "axios";
import {PortalPasswordAndQuestions, PortalPasswordResetValidationResult} from "components/src/api/portal/generated";
import {DEFAULT_PORTAL_PASSWORD_RESET_KEYS, PortalPasswordResetTranslationKeys} from "components/src/auth/PortalTranslationKeys";
import {RenderAlert} from "components/src/common/RenderAlert";
import {useTranslationWithOverride} from "components/src/hook/UseTranslationWithOverride";
import {logger} from "components/src/utils/Logger";
import {StringUtils} from "components/src/utils/StringUtils";
import {Utils} from "components/src/utils/Utils";
import DOMPurify from "dompurify";
import {useEffect, useRef, useState} from "react";
import Button from 'react-bootstrap/Button';
import FloatingLabel from "react-bootstrap/FloatingLabel";
import Form from 'react-bootstrap/Form';
import {useNavigate, useSearchParams} from "react-router-dom";

interface PortalPasswordResetProps {
    /**
     * Validates the reset code and returns the reset validation result. Provided by the
     * consuming app so this component can be reused with different back-end methods.
     */
    validatePortalPasswordReset: (resetCode: string) => Promise<{data: PortalPasswordResetValidationResult}>;
    /**
     * Resets the portal password for the given reset code. Provided by the consuming app so
     * this component can be reused with different back-end methods.
     */
    resetPortalPassword: (resetCode: string, portalPasswordAndQuestions: PortalPasswordAndQuestions) => Promise<unknown>;
    /**
     * Overrides for the i18next keys. Only the keys that need to differ have to be provided;
     * the rest fall back to {@link DEFAULT_PORTAL_PASSWORD_RESET_KEYS}.
     */
    translationKeys?: Partial<PortalPasswordResetTranslationKeys>;
}

export const PortalPasswordReset = ({validatePortalPasswordReset, resetPortalPassword, translationKeys}: PortalPasswordResetProps) =>
{
    const keys = {...DEFAULT_PORTAL_PASSWORD_RESET_KEYS, ...translationKeys};

    /**
     * Navigate is used to navigate to the main page after signup
     */
    const navigate = useNavigate();

    const [searchParams] = useSearchParams();
    /**
     * The base64 encoded reset validation code
     */
    const [resetCode, setResetCode] = useState<string>();
    /**
     * The result of the validation code
     */
    const [resetValidation, setResetValidation] = useState<PortalPasswordResetValidationResult>({email: "", securityQuestions: []});
    /**
     * The security answer for the user. Only set if security questions are enabled.
     */
    const [securityAnswer, setSecurityAnswer] = useState<string>();
    /**
     * The error message if some error occurred which is not a validation error
     */
    const [errorMessage, setErrorMessage] = useState<string>();
    /**
     * The password for the user
     */
    const [password, setPassword] = useState<string>();
    /**
     * The repeated password for the user
     */
    const [passwordRepeat, setPasswordRepeat] = useState<string>();
    /**
     * Ref for password input field
     */
    const passwordInputRef = useRef<HTMLInputElement>(null);
    /**
     * i18next translation hook
     */
    const {t} = useTranslationWithOverride();

    const sanitizeHTML = (html : string) => {
        return {__html: DOMPurify.sanitize(html, { ADD_ATTR: ['target'] })};
    }

    useEffect(() => {
        passwordInputRef.current?.focus();
    }, []);

    useEffect(() =>
    {
        const dParameter = searchParams.get("d");

        if (dParameter === null)
        {
            logger.warn("reset URL is missing the d parameter");

            setErrorMessage(t(keys.resetDataMissing));

            return;
        }

        validatePortalPasswordReset(dParameter)
            .then(response =>
            {
                setResetCode(dParameter);

                const resetValidation = response.data;

                setResetValidation(resetValidation);
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reset link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
            .finally(() => {
            })
    }, [searchParams, t, validatePortalPasswordReset, keys.resetDataMissing]);

    const resetPassword = () =>
    {
        if (resetCode === undefined) {
            throw new Error("resetCode is undefined");
        }

        if (password === undefined) {
            throw new Error("password is undefined");
        }

        if (password !== passwordRepeat)
        {
            setErrorMessage(t(keys.passwordMismatch));
            return;
        }


        resetPortalPassword(resetCode, {password: password,
            questionAndAnswers: [{question: resetValidation.securityQuestions[0], answer: securityAnswer}]}
        )
            .then(() => {
                    navigate("/");
            })
            .catch((err: Error | AxiosError) => {
                logger.error("Error validating reset link", err);
                setErrorMessage(Utils.getErrorMessage(err, t));
            })
    }

    const renderSecurityQuestion = () =>
    {
        if (resetValidation.securityQuestions.length === 0) {
            return undefined;
        }

        return (
            <>
                <p className="h4">{t(keys.securityQuestionHeader)}</p>

                <p>
                    {t([keys.securityQuestionText])}
                </p>

                <p className="h4">
                    {resetValidation.securityQuestions[0]}
                </p>

                <FloatingLabel label={t(keys.securityAnswerLabel)} className="text-secondary mb-3">
                    <Form.Control value={securityAnswer} placeholder="Security answer"
                                  onChange={e => setSecurityAnswer(e.target.value)}/>
                </FloatingLabel>
            </>
        )
    }

    const isResetButtonDisabled = () => {
        return StringUtils.isEmpty(resetValidation.email) ||
            StringUtils.isEmpty(password) ||
            (resetValidation.securityQuestions.length > 0 && StringUtils.isEmpty(securityAnswer));
    }

    return (
        <>
        {t([keys.header]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.header]))} />}
        <div className="w-100 p-4 shadow-sm rounded bg-body">
            <div className="mb-4 text-center">
                <h2>{t(keys.title)}</h2>
            </div>

            {/* show an alert if a validation error occurred */}
            <RenderAlert message={errorMessage}/>

            <FloatingLabel label={t(keys.emailLabel)} className="text-secondary mb-3">
                <Form.Control type="email" value={resetValidation.email} placeholder="Email"
                              disabled={true}/>
            </FloatingLabel>

            <FloatingLabel label={t(keys.passwordLabel)} className="text-secondary mb-3">
                <Form.Control type="password" value={password} placeholder="Password"
                              ref={passwordInputRef}
                              onChange={e => setPassword(e.target.value)}/>
            </FloatingLabel>

            <FloatingLabel label={t(keys.passwordRepeatLabel)} className="text-secondary mb-3">
                <Form.Control type="password" value={passwordRepeat} placeholder="Password Repeat"
                              onChange={e => setPasswordRepeat(e.target.value)}/>
            </FloatingLabel>

            {renderSecurityQuestion()}

            <Button variant="success" className="w-100 mt-4" disabled={isResetButtonDisabled()}
                    onClick={resetPassword}>
                {t(keys.setPasswordButton)}
            </Button>
        </div>
        {t([keys.footer]).length > 0 && <div dangerouslySetInnerHTML={sanitizeHTML(t([keys.footer]))} />}
       </>
    );
}
